/**
 * How far the tables of ranges have got.
 *
 * Reading one takes a minute - several megabytes arrive and about a million
 * rows are written - and the page which started it was answered at once. So
 * the page asks the portal every couple of seconds how far it is, and stops
 * asking the moment neither table is working.
 *
 * Without this the screen showed a button and nothing else, and whoever
 * pressed it had no way of telling whether anything was happening at all.
 */

import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** Seconds between two looks while something is being read. */
const ASK_EVERY = 2000;

interface Reading {
    readonly state: string;
    readonly progress: number;
    readonly doing: string | null;
    readonly ranges: number;
    readonly failure: string | null;
}

/** Draw one table's state onto its card. */
function draw(card: HTMLElement, reading: Reading): void {
    const working = reading.state === "WORKING";
    const bar = query<HTMLElement>("[data-geo-bar]", card);
    const shown = query<HTMLElement>("[data-geo-progress]", card);
    const doing = query<HTMLElement>("[data-geo-doing]", card);
    const button = query<HTMLButtonElement>("[data-geo-button]", card);
    const detail = query<HTMLElement>("[data-geo-detail]", card);

    if (shown) {
        shown.hidden = !working;
    }

    if (bar) {
        bar.style.width = `${working ? reading.progress : 100}%`;
    }

    if (doing) {
        doing.textContent = working && reading.doing ? text(reading.doing, "") : "";
    }

    if (button) {
        button.disabled = working;
    }

    if (detail && !working && reading.ranges > 0) {
        detail.textContent = `${reading.ranges} ${text("js.geo.ranges", "rozsahov")}`;
    }
}

/** Ask the portal how far the tables are, and keep asking while one works. */
export function initGeoProgress(): void {
    const panel = query<HTMLElement>("[data-geo-watch]");
    if (!panel) {
        return;
    }

    let timer: number | undefined;

    const look = async (): Promise<void> => {
        let answered: { sources: Record<string, Reading> };
        try {
            const answer = await fetch("/api/admin/protection/geography", {
                headers: { Accept: "application/json" },
            });
            if (!answer.ok) {
                return;
            }

            answered = await answer.json();
        } catch {
            // A look which fails is a look which is tried again; the page is
            // readable either way.
            return;
        }

        let working = false;
        for (const card of queryAll<HTMLElement>("[data-geo-source]", panel)) {
            const reading = answered.sources[card.dataset["geoSource"] ?? ""];
            if (!reading) {
                continue;
            }

            draw(card, reading);
            working = working || reading.state === "WORKING";
        }

        if (!working && timer !== undefined) {
            window.clearInterval(timer);
            timer = undefined;
            // One table which has just finished changes the wording of its
            // own button and the figures beside it, and the simplest honest
            // way to get both right is to draw the page again.
            window.location.reload();
        }
    };

    // Looked at once on arrival, in case a reading was started from another
    // browser, and then only while something is actually happening.
    void look().then(() => {
        if (queryAll<HTMLElement>("[data-geo-progress]:not([hidden])", panel).length > 0) {
            timer = window.setInterval(() => void look(), ASK_EVERY);
        }
    });
}
